import React, { Component } from 'react';
import Axios from 'axios';
import moment from 'moment';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import Colors from '../../../../styles/colors';
import { BaseButton, InputTextArea } from '../../../../styles/styled';
import commonStyles from '../../../../styles/commonStyles';
import { STATIC_HOMEPAGE_URL } from '../../../../config';
import { canUserAccessFeature } from '../../../../helpers/userinfoHelper';
import LimitWarning from '../../../../components/LimitWarning';
import { ALERTS } from '../../../../helpers/texts';
import { publisherManager, shopifyManager } from '../../../../helpers/managers';
import CustomDialog from '../custom_dialog';
import Loading from '../../../../components/Loading';
import { ActionButton } from '../../../../components/buttons/buttons';

const getShopifySessionExists = async (shop) => {
  try {
    const shopifySession = await shopifyManager.getProp(shop.trim().replace(/\./g, '(dot)'), 'session');
    return !isEmpty(shopifySession);
  } catch (e) {
    return false;
  }
};

const IntegrationView = props => {
  const {
    styles,
    connectionStatus,
    apiKey,
    mailChimpError = {},
    clearError = () => {},
    onApiKeyChange = () => {},
    saveIntegration = () => {},
    disconnectIntegration = () => {},
    loadingMainButtons = false,
    loadingClearError = false,
    history,
  } = props;
  const disableButtons = loadingMainButtons || loadingClearError;

  // check if feature allowed
  if (!canUserAccessFeature('MAILCHIMP_INTEGRATION')) {
    return (
      <IntegrationItemDivision>
        <span className="column-title">Status</span>
        <span className="column-inputs text-large font-weight-600 self-align center">
          <LimitWarning
            message={ALERTS().MAILCHIMP_INTEGRATION.NO_ACCESS}
            onClickUpgrade={() => history.push('/pricing')}
          />
        </span>
      </IntegrationItemDivision>
    );
  }

  const renderLabelState = (defaultText, processing) => {
    return processing ? (
      <Loading
        text="Processing"
        type="circle"
        align="center"
        size={18}
        fontSize={1.4}
        color={Colors.mainWhite}
        fill={Colors.mainWhite}
        textStyle={{ margin: '0 5px' }}
      />
    ) : (
      defaultText
    );
  };

  const renderMainButtons = () => {
    if (connectionStatus) {
      return (
        <MainButton
          disabled={disableButtons}
          className="btn btn-large"
          primaryColor={Colors.red}
          onClick={disconnectIntegration}>
          {renderLabelState('Disconnect', loadingMainButtons)}
        </MainButton>
      );
    }

    return (
      <MainButton
        disabled={disableButtons}
        className="btn btn-large"
        primaryColor={Colors.link}
        onClick={saveIntegration}>
        {renderLabelState('Save', loadingMainButtons)}
      </MainButton>
    );
  };

  const renderClearErrorButton = () => {
    if (isEmpty(mailChimpError)) {
      return null;
    }

    return (
      <ClearErrorButton
        disabled={disableButtons}
        className="btn btn-large"
        primaryColor={Colors.softBlack}
        onClick={clearError}>
        {renderLabelState('Try to clear error', loadingClearError)}
      </ClearErrorButton>
    );
  };

  return (
    <div>
      <IntegrationItemDivision>
        <span className="column-title">Status</span>
        <span className="column-inputs text-large font-weight-600 self-align center">
          <ConnectionStatus connected={connectionStatus}>
            {connectionStatus ? 'Connected' : 'Not Connected'}
          </ConnectionStatus>
        </span>
      </IntegrationItemDivision>
      <IntegrationItemDivision>
        <span className="column-title">API Key</span>
        <div className="column-inputs">
          <input type="text"
            readOnly={connectionStatus}
            style={styles.inputTitle}
            value={apiKey}
            onChange={onApiKeyChange}
          />
          <span>
            The API key for connecting with your MailChimp account.
            <a
              href="https://admin.mailchimp.com/account/api/"
              target="_blank"
              style={{ fontWeight: 600, color: Colors.mainOrange }}>
              {' '}
              Get your API key here.
            </a>
          </span>
        </div>
      </IntegrationItemDivision>

      {!isEmpty(mailChimpError) && (
        <IntegrationItemDivision>
          <span className="column-title">API Error</span>
          <div className="column-inputs">
            <InputTextArea
              style={{ color: Colors.red, borderColor: Colors.red, minHeight: 64 }}
              readOnly={true}
              value={mailChimpError.message}
            />
            <span>
              {`Access to the MileChimp API has been blocked${
                isEmpty(mailChimpError.createdAt)
                  ? '' 
                  : ' ' + moment.utc(mailChimpError.createdAt).fromNow()
              }.`}
              <a
                href="https://mailchimp.com/help/mailchimp-support-options/"
                target="_blank"
                style={{ fontWeight: 600, color: Colors.mainOrange }}>
                {' '}
                Please contact Mailchimp support here.
              </a>
            </span>
          </div>
        </IntegrationItemDivision>
      )}

      <IntegrationItemDivision>
        <span className="column-title sm-display-none">&nbsp;</span>
        <div className="column-inputs">
          {renderMainButtons()}{renderClearErrorButton()}
        </div>
      </IntegrationItemDivision>
    </div>
  );
};

export default class Integrations extends Component {
  constructor(props) {
    super(props);
    this.state = {
      integrationSelected: 'Mailchimp',
      soundwiseAPIkey: '',
      fetchingApiKey: true,
      apiKey: '',
      connectionStatus: false,
      loadingMainButtons: false,
      loadingClearError: false,
    };

    this.apiKey = '';
    this.saveIntegration = this.saveIntegration.bind(this);
    this.disconnectIntegrationProcessing = this.disconnectIntegrationProcessing.bind(this);
    this.disconnectIntegration = this.disconnectIntegration.bind(this);
    this.clearErrorProcessing = this.clearErrorProcessing.bind(this);
    this.clearError = this.clearError.bind(this); 
    this.retrieveMailChimpKey = this.retrieveMailChimpKey.bind(this);
    this.retrieveMailChimpError = this.retrieveMailChimpError.bind(this);
    this.retrieveShopifyState = this.retrieveShopifyState.bind(this);
    this.refreshShopifyState = this.refreshShopifyState.bind(this);
    this.onApiKeyChange = this.onApiKeyChange.bind(this);
    this.retrieveSoundwiseKey = this.retrieveSoundwiseKey.bind(this);
  }

  componentDidMount() {
    const { userInfo } = this.props;
    this.retrieveMailChimpKey(userInfo);
    this.retrieveMailChimpError(userInfo);
    this.retrieveShopifyState(userInfo);
    this.retrieveSoundwiseKey();
  }

  componentWillReceiveProps(nextProps) {
    const { userInfo } = nextProps;
    if (userInfo && userInfo.publisher) {
      if (userInfo.publisher.mailChimp != null) {
        if (
          this.props.userInfo.publisher &&
          this.props.userInfo.publisher.mailChimp &&
          userInfo.publisher.mailChimp
        ) {
          if (
            this.props.userInfo.publisher.mailChimp.apiKey != userInfo.publisher.mailChimp.apiKey
          ) {
            this.retrieveMailChimpKey(userInfo);
          }
        } else if (this.state.integrationSelected != 'YouTube' && userInfo.publisher.mailChimp) {
          this.retrieveMailChimpKey(userInfo);
        }
      }

      this.retrieveMailChimpError(userInfo);

      if (this.state.fetchingApiKey) {
        this.retrieveSoundwiseKey();
      }
    }
  }

  async retrieveMailChimpKey(userInfo) {
    const { id: publisherId } = userInfo.publisher;
    const mailChimp = await publisherManager.getProp(publisherId, 'mailChimp');
    if (isEmpty(mailChimp)) {
      return;
    }

    const mailChimpError = await publisherManager.getProp(publisherId, 'mailChimpError', {});

    const { apiKey } = mailChimp;
    this.apiKey = apiKey;
    this.setState({
      apiKey,
      connectionStatus: true,
      mailChimpError,
    });
  }

  async retrieveMailChimpError(userInfo) {
    const { id: publisherId } = userInfo.publisher;
    const mailChimpError = await publisherManager.getProp(publisherId, 'mailChimpError', {});
    this.setState({
      mailChimpError,
    });
  }

  async retrieveShopifyState(userInfo) {
    const { id: publisherId } = userInfo.publisher;
    const shopify = await publisherManager.getProp(publisherId, 'shopify', {});
    const { shop = '',  mUrl = '',} = shopify || {}
    if (!isEmpty(shop)) {
      const shopifyAppInstalled = await getShopifySessionExists(shop);
      this.setState({
        shopify,
        shopifyAppInstalled, 
      });
    }
  }

  refreshShopifyState() {
    const { userInfo } = this.props;
    this.retrieveShopifyState(userInfo);
  }

  retrieveSoundwiseKey() {
    const { userInfo } = this.props;
    const { soundwiseAPIkey, fetchingApiKey } = this.state;
    if (fetchingApiKey && isEmpty(soundwiseAPIkey) && 'id' in userInfo) {
      Axios.get(`/api/user/key/${userInfo.id}`)
        .then(response => {
          const { data: apiInfo } = response;
          if (!isEmpty(apiInfo)) {
            return this.setState({
              soundwiseAPIkey: apiInfo.key,
              fetchingApiKey: false,
            });
          }

          // create a new key, if data is empty
          Axios.post('/api/user/key', {
            userId: userInfo.id,
            publisherId: userInfo.publisherID,
          })
            .then(response => {
              const { data: apiResult = [] } = response;
              const [apiInfo = {}] = apiResult;
              if (!isEmpty(apiInfo)) {
                this.setState({
                  soundwiseAPIkey: apiInfo.key,
                  fetchingApiKey: false,
                });
              }
            })
            .catch(error => {
              const msg = this.getAxiosErrorResponse(error);
              console.error('Unable to create user API key', msg);
              this.setState({
                fetchingApiKey: false,
              });
            });
        })
        .catch(error => {
          const msg = this.getAxiosErrorResponse(error);
          console.error('Unable to fetch user API key', msg);
          this.setState({
            fetchingApiKey: false,
          });
        });
    }
  }

  getAxiosErrorResponse(error) {
    if ('response' in error) {
      const { data } = error.response;
      if (!data.success) {
        return data.message;
      }

      return data;
    }

    return error;
  }

  async saveIntegration() {
    const { userInfo } = this.props;
    const { integrationSelected = '', apiKey = '' } = this.state;
    const hasSelected = !isEmpty(integrationSelected);

    if (hasSelected && isEmpty(apiKey)) {
      return this.showAlertModal({
        content: `Please enter the ${integrationSelected} API key.`,
      });
    }

    if (hasSelected && apiKey !== this.apiKey) {
      this.setState({ loadingMainButtons: true });
      try {
        const { id: publisherId } = userInfo.publisher;
        await Axios.post('/api/integration/mailchimp', {
          publisherId,
          apiKey,
        });

        // As firebase sends realtime notifications, we do not really need this, but what the heck!
        this.apiKey = apiKey;
        this.setState({ connectionStatus: true });
        this.showAlertModal({
          content: `You've successfully connected your ${integrationSelected} account.`,
        });
      } catch (err) {
        console.error('Integration error', err.message, integrationSelected);
        this.showAlertModal({
          content: `You've entered an invalid ${integrationSelected} API key. Please try again.`,
        });
      } finally {
        this.setState({ loadingMainButtons: false });
      }
    }
  }

  async disconnectIntegrationProcessing() {
    const { userInfo } = this.props;
    const { apiKey, integrationSelected } = this.state;
    const { id: publisherId } = userInfo.publisher;

    this.closeAlertModal();
    this.setState({ loadingMainButtons: true });
    try {
      await Axios.delete('/api/integration/mailchimp', {
        params: {
          apiKey,
          publisherId,
        },
      });

      this.apiKey = '';
      this.setState({ apiKey: '', connectionStatus: false, mailChimpError: {} });
      this.showAlertModal({
        content: `You've successfully disconnected your ${integrationSelected} account.`,
      });
    } catch (err) {
      console.error('Integration removal error', err.message, integrationSelected);
      this.showAlertModal({
        content: `Failed to disconnect the ${integrationSelected} integration. Please try again.`,
      });
    } finally {
      this.setState({ loadingMainButtons: false });
    }
  }

  disconnectIntegration() {
    const { integrationSelected = '' } = this.state;
    this.showAlertModal({
      content: `Are you sure you want to disconnect your ${integrationSelected} account? This action cannot be undone.`,
      actions: [
        <div className="padding-20px text-center">
          <ActionButton onClick={this.disconnectIntegrationProcessing}>Confirm</ActionButton>
          <ActionButton
            onClick={() => this.closeAlertModal()}
            textColor="#f76b1c"
            backgroundColor="#fff">
            Cancel
          </ActionButton>
        </div>,
      ],
    });
  }

  async clearErrorProcessing() {
    const { userInfo } = this.props;
    const { apiKey, integrationSelected = '' } = this.state;
    const { id: publisherId } = userInfo.publisher;

    this.closeAlertModal();
    this.setState({ loadingClearError: true });
    let content = null;
    try {
      const result = await Axios.post('/api/integration/mailchimp', {
        apiKey,
        publisherId,
        checkErrorOnly: true,
      });

      const { data = {} } = result || {};
      const { success = false, message = '' } = data;
      if (success) {
        content = `You've successfully clear API error the ${integrationSelected} integration.`;
        this.setState({ mailChimpError: {} })
      } else {
        content = `Failed to clear API error the ${integrationSelected} integration.${
            isEmpty(message) ? `` : ` An error occurred while checking: "${message}".`
          } Please try again or contact ${integrationSelected} support.`;
      }
    } catch (err) {
      console.error('Integration checking error', err.message, integrationSelected);
      content = `Failed to clear API error the ${integrationSelected} integration.` +
        ` Please try again or contact ${integrationSelected} support.`;
    } finally {
      this.showAlertModal({ content });
      this.setState({ loadingClearError: false });
    }
  }

  clearError() {
    const { integrationSelected = '' } = this.state;
    this.showAlertModal({
      content: 
        `Are you sure you want to check your ${integrationSelected} account` +
        ` and try to resolve the API error if possible?`,
      actions: [
        <div className="padding-20px text-center">
          <ActionButton onClick={this.clearErrorProcessing}>Confirm</ActionButton>
          <ActionButton
            onClick={() => this.closeAlertModal()}
            textColor="#f76b1c"
            backgroundColor="#fff">
            Cancel
          </ActionButton>
        </div>,
      ],
    });
  }

  showAlertModal(metadata = {}) {
    this.alertDialog.showModal(metadata);
  }

  closeAlertModal() {
    this.alertDialog.closeModal();
  }

  onApiKeyChange(e) {
    this.setState({ apiKey: e.target.value });
  }

  renderAPIKeyInput() {
    const { isLoggedIn, userInfo } = this.props;
    const { soundwiseAPIkey, fetchingApiKey } = this.state;

    // check for accessibility, for now its zapier integration
    const accessible = canUserAccessFeature('INTEGRATION_3RDPARTY_ZAPIER');
    if (accessible && !isEmpty(soundwiseAPIkey)) {
      return (
        <input
          type="text"
          className="api-key-input"
          style={styles.inputTitle}
          value={soundwiseAPIkey}
          readOnly
        />
      );
    }

    return (
      <BlockButton
        background="#f76b1c"
        color="#fff"
        onClick={() => {
          this.props.showAlertModal({
            title: 'Upgrade Required',
            content: ALERTS().INTEGRATION_3RDPARTY_ZAPIER.NO_ACCESS,
            upgrade: true,
          });
        }}
        disabled={!isLoggedIn || !isObject(userInfo.publisher) || fetchingApiKey}>
        {!fetchingApiKey ? 'Get my Soundwise API key' : 'Waiting for API key...'}
      </BlockButton>
    );
  }

  render() {
    const {
      integrationSelected = '',
      loadingMainButtons = false,
      loadingClearError = false,
      shopify = {},
      shopifyAppInstalled = false,
    } = this.state;
    const { userInfo, history } = this.props;
    const apiAccessible = canUserAccessFeature('INTEGRATION_3RDPARTY_ZAPIER');

    const shopifyViewEnabled = !isEmpty(shopify);
    const { mUrl = '',} = shopify || {}

    return (
      <IntegrationWrapper>
        <IntegrationContainer>
          <div className="row">
            <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12" style={{ minHeight: 700 }}>
              <MyAPISection>
                <span className="column-title">My API Key</span>
                <div className="column-inputs">
                  {this.renderAPIKeyInput()}
                  <div className="api-key-sub">
                    <span className="api-key-sub-title">The API key of my Soundwise account</span>
                    <a
                      className="api-key-info"
                      href={`${STATIC_HOMEPAGE_URL}/blog/how-to-connect-soundwise-to-zapier-for-exporting-and-enrolling-new-listeners`}
                      target="_blank"
                      title="Learn more">
                      <i className="material-icons">info_outlined</i>
                    </a>
                  </div>
                  {apiAccessible && (
                    <div className="api-key-sub">
                      <span className="api-key-sub-title">The Soundwise Partners API</span>
                      <a
                        className="api-key-info"
                        href={`${STATIC_HOMEPAGE_URL}/api-docs`}
                        target="_blank"
                        title="Learn more">
                        <i className="material-icons">info_outlined</i>
                      </a>
                    </div>
                  )}
                </div>
              </MyAPISection>

              <IntegrationItemDivision>
                <span className="column-title">Integrate with</span>
                <div className="column-inputs dropdown">
                  <div className="btn dropdown-toggle" data-toggle="dropdown">
                    <div style={styles.dropdownTitle}>
                      <span style={{ ...styles.titleText, padding: '20px' }}>
                        {integrationSelected != '' ? integrationSelected : `Choose Integration`}
                      </span>
                      <span
                        style={{ position: 'absolute', right: 10, top: 20 }}
                        className="caret"
                      />
                    </div>
                  </div>
                  <ul className="dropdown-menu" style={{ width: '100%', padding: 0 }}>
                    <li style={{ fontSize: '16px', width: '100%' }}>
                      <button
                        style={{ ...styles.categoryButton, width: '100%' }}
                        onClick={() => this.setState({ integrationSelected: 'Mailchimp' })}>
                        Mailchimp
                      </button>
                    </li>
                  </ul>
                </div>
              </IntegrationItemDivision>
              <IntegrationViewSection>
              {integrationSelected === '' ? null : (
                <IntegrationView
                  integrationSelected={integrationSelected}
                  styles={styles}
                  connectionStatus={this.state.connectionStatus}
                  apiKey={this.state.apiKey}
                  mailChimpError={this.state.mailChimpError}
                  onApiKeyChange={this.onApiKeyChange}
                  saveIntegration={this.saveIntegration}
                  disconnectIntegration={this.disconnectIntegration}
                  clearError={this.clearError}
                  existingApiKey={this.apiKey}
                  userInfo={userInfo}
                  history={history}
                  loadingMainButtons={loadingMainButtons}
                  loadingClearError={loadingClearError}
                />
              )}
              </IntegrationViewSection>

              {shopifyViewEnabled && (
                <>
                  <IntegrationItemDivision>
                    <span className="column-title">Integrate with</span>
                    <div className="column-inputs dropdown"
                      onClick={this.refreshShopifyState}
                      title="Click to refresh Shopify connection status"
                    >
                      <div className="btn dropdown-toggle" data-toggle="dropdown">
                        <div style={styles.dropdownTitle}>
                          <span style={{ ...styles.titleText, padding: '20px' }}>
                            Shopify
                          </span>
                          <span style={{ position: 'absolute', right: 5 }}>
                            <i className="material-icons">refresh</i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </IntegrationItemDivision>

                  <IntegrationItemDivision>
                    <span className="column-title">Status</span>
                    <span className="column-inputs text-large font-weight-600 self-align center">
                      <ConnectionStatus connected={shopifyAppInstalled}>
                        {shopifyAppInstalled ? 'Connected' : 'Not Connected'}
                      </ConnectionStatus>
                    </span>
                  </IntegrationItemDivision>
                  <IntegrationItemDivision>
                    <span className="column-title">App</span>
                      <div className="column-inputs text-large font-weight-600 self-align center">
                        <a
                          href={mUrl || ''}
                          target="_blank"
                          style={{ fontWeight: 600, color: Colors.mainOrange }}>
                          {' '}
                          Merchant install link
                        </a>
                      </div>
                  </IntegrationItemDivision>
                </>
              )}
            </div>
          </div>
          <CustomDialog ref={ref => (this.alertDialog = ref)} />
        </IntegrationContainer>
      </IntegrationWrapper>
    );
  }
}

const IntegrationWrapper = styled.div`
  padding: 30px;
  background: #fff;
`;

const IntegrationContainer = styled.div`
  max-width: 720px;
`;

const IntegrationItemDivision = styled.div`
  width: 100%;
  margin-top: 10px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;

  .column-title {
    width: 150px;
    font-size: 16px;
    font-weight: 600;
    padding: 10px 0;
  }

  .column-inputs {
    flex: 1;

    &.self-align {
      &.center {
        align-self: center;
      }

      &.left {
        align-self: flex-start;
      }

      &.right {
        align-self: flex-end;
      }
    }

    &.dropdown {
      .dropdown-toggle {
        width: 100%;
        padding: 0px;
        border: 1px solid #dfdfdf;
      }
    }
  }

  @media (max-width: 991px) {
    flex-direction: column;

    .column-title {
      width: 100%;
    }

    .column-inputs {
      &.self-align {
        &.center {
          align-self: flex-start;
        }
      }
    }
  }
`;

const ConnectionStatus = styled.span`
  line-height: 1;
  white-space: nowrap;
  border-radius: 0.25em;
  font-size: 90%;
  font-weight: bold;
  display: inline;
  padding: 0.2em 0.6em 0.3em;
  color: #fff;

  background-color: ${props => (props.connected ? '#24b47e' : '#e25950')};
`;

const MyAPISection = styled(IntegrationItemDivision)`
  padding-bottom: 20px;
  border-bottom: 2px dashed #e2e2e2;

  .column-inputs {
    .api-key-input {
      flex-grow: 1;
    }

    .api-key-sub {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      margin-top: 2px;

      .api-key-info {
        width: 20px;
        height: 20px;
        margin-left: 5px;

        i.material-icons {
          font-size: 1.6em;
        }
      }

      .api-key-sub-btn {
        background-color: #f76a1c;
        border: 1px solid #f76a1c;
        color: #fff;
        padding 5px 10px;
        border-radius: 5px;

        &[disabled] {
          background-color: #ffab7d;
          border: 1px solid #ffab7d;
        }
      }
    }
  }
`;

const IntegrationViewSection = styled(IntegrationItemDivision)`
  padding-bottom: 20px;
  border-bottom: 2px dashed #e2e2e2;
`;

const BlockButton = styled.button`
  width: 100%;
  line-height: 30px;
  background-color: ${props => props.background || '#f76b1c'};
  border: 1px solid ${props => props.background || '#f76b1c'};
  color: ${props => props.color || '#fff'};
  border-radius: 5px;
  font-size: 14px;
  font-weight: bold;
  padding: 6px 20px;

  &[disabled] {
    opacity: 0.6;
  }
`;

const styles = {
  titleText: { ...commonStyles.titleText },
  inputTitle: { ...commonStyles.inputTitle, fontSize: 16 },
  dropdownTitle: { ...commonStyles.dropdownTitle },
  categoryButton: { ...commonStyles.categoryButton },
  muted: {
    fontSize: 16,
    color: Colors.fontGrey,
    fontWeight: 'regular',
    verticalAlign: 'middle',
    wordWrap: 'break-word',
  },
};

const MainButton = styled(BaseButton)`
  width: 200px;
`;

const ClearErrorButton = styled(BaseButton)`
  width: 200px;
  margin-left: 20px;
`;
